import React, { FC, useState, useEffect } from 'react'
import { ScrollView, TouchableOpacity, View, Text } from 'react-native'
import { useNavigate } from 'react-router-dom'

import {
  AppBackground,
  BottomActionContainer,
  Button,
  ResponsiveLogo,
} from '@ui/components'

import {
  Headline2,
  Headline5,
  BodyText,
} from '@/aena/styles'

import { ReactComponent as BackIcon } from '@/assets/icons/back_icon.svg'
import { ReactComponent as PlaneIcon } from '@/assets/icons/paw_color.svg'
import { ReactComponent as AddIcon } from '@/assets/icons/paw.svg'
import { travelApi, TravelLogic, type ITravelData } from '@front/logic'
import { useAppSelector } from '@front/store'
import { getHomeInfo } from '@front/apis'
import { t } from 'i18next'
import { HeaderWithBackAena } from '@/components/headerwithback'

export const TravelList: FC = () => {
  const navigate = useNavigate()
  const { guid: userGUID } = useAppSelector((state) => state.selectedPet)
  const ownerCard = useAppSelector((state) => state.user.ownerCardNumber)
  const [travels, setTravels] = useState<ITravelData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Si no hay pet seleccionado en el store, redirigir al home para configurar correctamente
    if (!userGUID) {
      console.debug('No selected pet found, redirecting to home')
      navigate('aena/home')
      return
    }
    loadTravels()
  }, [userGUID])

  const loadTravels = async () => {
    if (!userGUID) {
      setError('No hay mascota seleccionada')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await travelApi.getTravels({ userGUID })
      setTravels(response.data.travels)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar los viajes')
      console.error('Error loading travels:', err)
    } finally {
      setLoading(false)
    }
  }

  const TravelCard: React.FC<{ travel: ITravelData; onPress: () => void }> = ({ travel, onPress }) => {
    const route = TravelLogic.generateRouteText(travel.departureAirport, travel.arrivalAirport)

    return (
      <TouchableOpacity onPress={onPress} style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
        borderLeftWidth: 4,
        borderLeftColor: TravelLogic.getStatusColor(travel.status)
      }}>
        {/* Header con fecha y estado */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}>
          <View style={{
            backgroundColor: '#dc3545',
            paddingHorizontal: 12,
            paddingVertical: 4,
            borderRadius: 16
          }}>
            <Text style={{ color: "currentColor" }}>
              Viaje a {travel.arrivalAirport.split(' ')[0]} {TravelLogic.formatShortDate(travel.flightDate)}
            </Text>
          </View>

          <View style={{
            backgroundColor: TravelLogic.getStatusColor(travel.status),
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 12
          }}>
            <Text style={{ color: "currentColor" }}>
              {TravelLogic.getStatusText(travel.status)}
            </Text>
          </View>
        </View>

        {/* Información del vuelo */}
        <View style={{ marginBottom: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
            <PlaneIcon width={16} height={16} />
            <BodyText style={{ fontWeight: '600' }}>
              {travel.airline} ({travel.flightNumber})
            </BodyText>
          </View>

          <BodyText style={{ color: "currentColor" }}>
            ({travel.pets.length}) Mascota{travel.pets.length !== 1 ? 's' : ''} ({travel.pets.length})
          </BodyText>

          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
            <PlaneIcon width={12} height={12} />
            <BodyText style={{ color: "currentColor" }}>
              {route}
            </BodyText>
            <View style={{
              backgroundColor: '#f0f0f0',
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 8,
              marginLeft: 'auto'
            }}>
              <PlaneIcon width={14} height={14} />
            </View>
          </View>
        </View>

        {/* Mascotas */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingTop: 8,
          borderTopWidth: 1,
          borderTopColor: '#f0f0f0'
        }}>
          {travel.pets.map((pet: any, index: number) => (
            <View key={pet.guid || index} style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginRight: 16
            }}>
              <Text style={{ fontSize: 20, marginRight: 4 }}>🐕</Text>
              <BodyText style={{ fontSize: 14, color: '#666' }}>
                {pet.name}
              </BodyText>
            </View>
          ))}
        </View>
      </TouchableOpacity>
    )
  }


  const handleTravelPress = (travelId: string) => {
    navigate(`detail/${travelId}`)
  }

  const handleNewTravel = () => {
    navigate('add')
  }

  if (loading) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <BodyText>Cargando viajes...</BodyText>
        </View>
      </AppBackground>
    )
  }

  if (error) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <BodyText style={{ color: "currentColor" }}>
            {error}
          </BodyText>
          <Button
            title="Reintentar"
            onPress={loadTravels}
            size="medium"
            textType="cta_medium"
            color="primary"
          />
        </View>
      </AppBackground>
    )
  }

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena title="Mis viajes" />

        <BottomActionContainer
          bottomAction={
            <Button
              title="Nuevo viaje"
              icon={<AddIcon width={24} height={24} />}
              size="block"
              textType="cta_medium"
              borderRadius={5}
              buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
              allowIconAndText
              color="primary"
              onPress={handleNewTravel}
            />
          }
        >
          <ScrollView style={{ padding: 16 }}>
            {travels.length === 0 ? (
              <View style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                paddingVertical: 60
              }}>
                <PlaneIcon width={64} height={64} />
                <Headline5 style={{ color: "currentColor" }}>
                  No tienes viajes registrados
                </Headline5>
                <BodyText style={{ color: "currentColor" }}>
                  Crea tu primer viaje para empezar
                </BodyText>
              </View>
            ) : (
              <>
                <View style={{ marginBottom: 16 }}>
                  <Headline2 style={{ color: "currentColor" }}>
                    Tus Viajes
                  </Headline2>
                  <BodyText style={{ color: "currentColor" }}>
                    {travels.length} viaje{travels.length !== 1 ? 's' : ''} registrado{travels.length !== 1 ? 's' : ''}
                  </BodyText>
                </View>

                {travels.map(travel => (
                  <TravelCard
                    key={travel.id}
                    travel={travel}
                    onPress={() => handleTravelPress(travel.id)}
                  />
                ))}
              </>
            )}
          </ScrollView>
        </BottomActionContainer>
      </View>
    </AppBackground>
  )
}

export default TravelList
